import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import StatusPill from "../../Budgets/components/StatusPill";
import { fmtEur, fmtEurSigned } from "./format";

// TONE_CLASS mirrors PerformanceTableBlock/DriverTableBlock's mapping of the
// adverse/favorable/neutral vocabulary (backend_go internal/boardfigures/kpi.go's
// varianceTone) to a colour class: a horizon forecast that runs over its budget
// (variance > 0) is adverse (red), one that runs under (variance < 0) is favorable (green).
const TONE_CLASS = {
  adverse: "a4-kpi-tone-adverse",
  favorable: "a4-kpi-tone-favorable",
  neutral: "a4-kpi-tone-neutral",
};

function varianceTone(variance) {
  if (variance > 0) return "adverse";
  if (variance < 0) return "favorable";
  return "neutral";
}

// PILL_STATUS maps boardfigures.HorizonRow's status vocabulary ("over" | "at_risk" |
// "on_track" - internal/boardfigures/forecast.go's BuildHorizon) onto StatusPill's own
// STATUS_CONFIG keys (src/views/Budgets/components/StatusPill.jsx). "over" and "at_risk"
// are literal StatusPill keys already ("Over budget" / "At risk"); "on_track" has no
// identically-named entry, so it maps onto StatusPill's "green" key, which renders the
// same "On track" label with a success tone.
const PILL_STATUS = { over: "over", at_risk: "at_risk", on_track: "green" };

function pillStatus(status) {
  return PILL_STATUS[status] || status;
}

function capitalize(s) {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const columns = [
  { Header: "Horizon", accessor: "horizon", Cell: ({ value }) => `${value} days` },
  { Header: "Forecast", accessor: "forecast", align: "right", Cell: ({ value }) => fmtEur(value) },
  { Header: "Budget", accessor: "budget", align: "right", Cell: ({ value }) => fmtEur(value) },
  {
    Header: "Variance",
    accessor: "variance",
    align: "right",
    Cell: ({ value }) => (
      <span className={TONE_CLASS[varianceTone(value)]}>{fmtEurSigned(value)}</span>
    ),
  },
  { Header: "Confidence", accessor: "confidence", Cell: ({ value }) => capitalize(value) },
  {
    Header: "Status",
    accessor: "status",
    align: "center",
    Cell: ({ value }) => <StatusPill status={pillStatus(value)} />,
  },
];

/**
 * HorizonTableBlock - 30/60/90-day forecast-vs-budget summary (horizon_table block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/horizon -> envelope key
 * "horizon" -> boardfigures.Horizon { rows: [HorizonRow] } (Task 5's BuildHorizon), exactly
 * one row per requested horizon (normally 30, 60, 90) with a sign-derived variance/status.
 */
const HorizonTableBlock = ({ title = "Forecast horizon", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/horizon`
  );

  const rows = data?.horizon?.rows ?? [];

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-horizon-table">
      <FlexTable columns={columns} data={rows} getRowId={(row) => row.horizon} />
    </BaseBlock>
  );
};

HorizonTableBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default HorizonTableBlock;
